import { use } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { useQueryClient } from '@tanstack/react-query';
import { jwtDecode } from 'jwt-decode';
import { User } from 'oidc-client-ts';
import Routes from 'shared/enums/routes';
import TokenProfile from 'shared/features/auth/authentication/models/token-profile';
import AuthProviderContext from 'shared/features/auth/authentication/providers/contexts/auth-provider-context';
import { useLogging } from 'shared/logging/providers/logging.provider';

import { Tenant } from 'features/tenants/models/tenant';
import { TenantAccess } from 'features/tenants/models/tenant-access';

export type TenantAccessIndicatorState = {
  originalUser: User | undefined;
  temporaryTenantAccess: (accessToken: TenantAccess, tenant: Tenant | null) => void;
  revertTemporaryTenantAccess: () => void;
};

const PortalTitle = 'Sonevo Portal';

const useTenantAccessIndicatorState = (): TenantAccessIndicatorState => {
  const queryClient = useQueryClient();
  const { log } = useLogging();
  const navigate = useNavigate();
  const auth = useAuth();
  const {
    logout,
    tenant: { originalUser, setOriginalUser, storeTenantUser, revertToDefaultTenant },
  } = use(AuthProviderContext);

  const temporaryTenantAccess = async (accessToken: TenantAccess, tenant: Tenant | null): Promise<void> => {
    if (!originalUser) {
      setOriginalUser(auth.user ?? undefined);
    }

    const decodedToken = jwtDecode<TokenProfile>(accessToken.accessToken);

    const newUser = new User({
      id_token: accessToken.idToken,
      session_state: accessToken.sessionState,
      access_token: accessToken.accessToken,
      refresh_token: accessToken.refreshToken,
      token_type: accessToken.tokenType,
      scope: accessToken.scope,
      profile: {
        sub: decodedToken.sub,
        azp: decodedToken.azp,
      } as never,
      expires_at: decodedToken.exp,
    });

    await storeTenantUser(newUser);
    await auth.signinSilent();

    document.title = `${PortalTitle} (${tenant?.displayName})`;
    navigate(Routes.Organisation);
  };

  const revertTemporaryTenantAccess = async (): Promise<void> => {
    const revertedSuccessfully = await revertToDefaultTenant();
    if (revertedSuccessfully === true) {
      queryClient.clear();
      log('Successfully reverted to default tenant', SeverityLevel.Information);
      document.title = PortalTitle;
      navigate(Routes.Tenants);
    } else {
      log(`Error reverting to default tenant, logging out gracefully`, SeverityLevel.Error);
      await logout();
    }
  };

  return {
    originalUser,
    temporaryTenantAccess,
    revertTemporaryTenantAccess,
  };
};

export default useTenantAccessIndicatorState;
